<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <b-dropdown id="unduh-dropdown" right variant="primary" menu-class="dropdown-menu-end">
              <template v-slot:button-content>
                <i class="mc-fa icon-outline_download"></i>{{ $t('global.action.download') }}
              </template>
              <b-dropdown-item href="#" @click="downloadReport('xlsx')">
                <img :src="excelIcon" alt="excel" />
                {{ $t('global.action.downloadExcel') }}
              </b-dropdown-item>
              <b-dropdown-item href="#" @click="downloadReport('pdf')">
                <img :src="pdfIcon" alt="pdf" />
                {{ $t('global.action.downloadPdf') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col v-for="item of summaryFields" class="pt-2" sm="14" md="2" :key="`key-${item.key}`">
          <b-form-group
            :id="`${item.key}_label`"
            :label="`${item.label}`"
            :label-for="`${item.key}`"
          >
            <span v-if="!isBusy">
              {{ returnValueSummary(item) }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      searchable
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="false"
      reportKey="summary-alert"
      v-if="Object.keys(filters).length > 0"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>

      <template #cell()="{ field, item }">
        <p v-if="field.key === 'vehicle'">{{ item ? item.vehicle?.licensePlate : '-' }}</p>
        <p v-else-if="field.key === 'driver'">{{ item ? item.driver?.fullname : '-' }}</p>
        <p v-else-if="field.key === 'totalDistance'">
          {{ item ? item.totalDistance.toFixed(2) : '-' }}
        </p>
        <p v-else>
          {{
            item.alerts?.find((f) => f.alertName === field.key)
              ? item.alerts?.find((f) => f.alertName === field.key).totalAlert
              : '-'
          }}
        </p>
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import { GET_SUMMARY_ALERT, EXPORT_REPORT_SUMMARY_ALERT } from '@/services/api/report/report'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import McBadge from '@/components/McBadge'
import { mapGetters } from 'vuex'
import { MixPanel } from '@/mixin/mixpanel'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    McBadge,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      summary: {},
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'summary-alert',
      filterDetail: {},
      summaryKey: 50,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
    }
  },
  mounted() {
    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    childFields() {
      const filteredData = this.fields.filter((obj) => {
        return Object.prototype.hasOwnProperty.call(obj, 'children')
      })

      return filteredData
    },
    fields() {
      return this.reportFields
    },
    columns() {
      const finalFields = []

      this.reportFields.forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(field, 'children')) {
          finalFields.push(...field.children)
        } else {
          finalFields.push(field)
        }
      })
      return finalFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    summaryFields() {
      let summaryFields = [
        { key: 'totalDistance', label: this.$t('report.fields.totalDistance'), unit: 'Km' },
        { key: 'HARSH_ACCELERATION', label: this.$t('global.alert.suddenAcceleration'), unit: '' },
        { key: 'HARSH_BRAKING', label: this.$t('global.alert.suddenDeceleration'), unit: '' },
        { key: 'OVERSPEED', label: this.$t('global.alert.overspeed'), unit: '' },
        { key: 'SHARP_TURN', label: this.$t('global.alert.sharpTurn'), unit: '' },
        { key: 'SOS_SENT', label: this.$t('global.alert.sosButton'), unit: '' },
        { key: 'DEVICE_DISARMED', label: this.$t('global.alert.gpsDisarmed'), unit: '' },
        { key: 'TEMPERATURE_1', label: this.$t('global.alert.temp1'), unit: '' },
        { key: 'TEMPERATURE_2', label: this.$t('global.alert.temp2'), unit: '' },
        { key: 'GSM_SIGNAL_WEAK', label: this.$t('global.alert.gpsWeakSignal'), unit: '' },
        { key: 'VIBRATION', label: this.$t('global.alert.vibration'), unit: '' },
        { key: 'ENGINE_ON', label: this.$t('report.fields.engineOn'), unit: '' },
        { key: 'ENGINE_OFF', label: this.$t('report.fields.engineOff'), unit: '' },
        { key: 'ENTER_GEOFENCE', label: this.$t('report.fields.enterGeofence'), unit: '' },
        { key: 'EXIT_GEOFENCE', label: this.$t('report.fields.exitGeofence'), unit: '' },
        { key: 'PTO_ON', label: this.$t('report.fields.ptoOn'), unit: '' },
        { key: 'PTO_OFF', label: this.$t('report.fields.ptoOff'), unit: '' },
      ]
      if (this.hasAccess(['raw_alerts_dms:read'])) {
        summaryFields.splice(
          5,
          0,
          { key: 'YAWN', label: this.$t('global.alert.yawning'), unit: '' },
          { key: 'DISTRACTION', label: this.$t('global.alert.disturbance'), unit: '' },
          { key: 'SMOKING', label: this.$t('global.alert.smoking'), unit: '' },
          { key: 'PHONE_CALLING', label: this.$t('global.alert.calling'), unit: '' },
          { key: 'EYE_CLOSED', label: this.$t('global.alert.asleep'), unit: '' },
          { key: 'MISSING_FACE', label: this.$t('global.alert.outOfFrame'), unit: '' },
          { key: 'CRASH', label: this.$t('global.alert.crash'), unit: '' }
        )
      }
      if (this.hasAccess(['fatigue_driving_alert:read'])) {
        summaryFields.splice(13, 0, {
          key: 'FATIGUE_DRIVING',
          label: this.$t('global.alert.fatigueDriving'),
          unit: '',
        })
      }
      if (this.hasAccess(['seat_belt_alert:read'])) {
        summaryFields.splice(13, 0, {
          key: 'SEATBELT',
          label: this.$t('global.alert.seatbelt'),
          unit: '',
        })
      }
      if (this.hasAccess(['teltonika-device:read'])) {
        summaryFields.push({
          key: 'CRASH_DETECTION',
          label: this.$t('global.alert.crashDetection'),
          unit: '',
        })
      }
      if (this.hasAccess(['raw_alerts_adas:read'])) {
        summaryFields = summaryFields.concat([
          {
            key: 'FORWARD_COLLISION_WARNING',
            label: this.$t('global.alert.forwardCollison'),
            unit: '',
          },
          {
            key: 'LANE_DEPARTURE_WARNING',
            label: this.$t('global.alert.laneDeparture'),
            unit: '',
          },
          {
            key: 'HEADWAY_MONITOR_WARNING',
            label: this.$t('global.alert.headwayMonitor'),
            unit: '',
          },
          {
            key: 'HUMAN_DETECTION',
            label: this.$t('global.alert.humanDetection'),
            unit: '',
          },
        ])
      }
      if (this.hasAccess(['infrared_blocked_alert:read'])) {
        summaryFields.push({
          key: 'INFRARED_BLOCKED',
          label: this.$t('global.alert.infraredBlocked'),
          unit: '',
        })
      }
      if (this.hasAccess(['front_object_detected_alert:read'])) {
        summaryFields.push({
          key: 'FRONT_OBJECT_DETECTED',
          label: this.$t('global.alert.frontObjectDetected'),
          unit: '',
        })
      }
      return summaryFields
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const filters = {}
        Object.keys(this.filters).forEach((item) => {
          if (
            this.filters[item] !== null &&
            ((Array.isArray(this.filters[item]) && this.filters[item].length > 0) ||
              (!Array.isArray(this.filters[item]) && this.filters[item]))
          ) {
            filters[item] = this.filters[item]
          }
        })
        const { periode, ...otherFilter } = filters
        const startDate = periode.length > 0 ? moment(periode[0]).toISOString() : null
        const endDate = periode.length > 0 ? moment(periode[1]).toISOString() : null
        const model = {
          startDate,
          endDate,
          ...otherFilter,
        }
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    apiClient() {
      return {
        get: async (filter) => {
          const { valid, model } = this.generateFilter
          if (!valid) return {}
          const self = this
          self.isBusy = true
          const res = await this.queryCache.fetch(
            GET_SUMMARY_ALERT,
            {
              filter: {
                ...model,
                column: this.columns,
                ...filter,
              },
            },
            self.reportKey
          )
          self.isBusy = false
          const result = res.summaryAlert.summaryAlert
          self.message = res.summaryAlert.message
          self.isError = !res.summaryAlert.isSuccessful
          self.modalVerifReport += 1
          self.totalRows = result?.pagination?.total ?? 0
          self.summary = result?.summary ?? {}
          self.summary.totalAlert.forEach((alert) => {
            self.summary[alert.alertName] = alert.totalAlert ?? 0
          })
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDateShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDateShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (this.filters.vehicleId && this.filters.vehicleId.length > 0) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId && this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
  },
  methods: {
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    returnValueSummary(item) {
      if (Object.keys(this.summary).length > 0) {
        return this.formattingValue(this.summary[item.key], item.unit) ?? '-'
      } else {
        return '-'
      }
    },
    formattingValue(value, unit) {
      if (!value) return null
      else if (unit === 'duration') {
        const duration = value.split(':')
        return `${duration[0]} ${this.$t('global.time.hours')}, ${duration[1]} ${this.$t(
          'global.time.minutes'
        )}, ${duration[2]} ${this.$t('global.time.seconds')}`
      } else {
        return `${value} ${unit}`
      }
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name: `${this.title} ` + moment(periode[1]).format('MMM YYYY') + `.${extension}`,
        periode: moment(periode[1]).format('MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension,
        id: result.summaryAlertReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension) {
      this.track(`Download ${this.report.event} ${extension == 'pdf' ? '- PDF' : '- Excel'}`)

      const { valid, model } = this.generateFilter
      if (!valid) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        column: this.columns,
        subtitle: `Periode : ${this.momentMMM(this.momentM(periode[1]))} ${this.momentY(
          periode[1]
        )}`,
        downloadAs: extension == 'pdf' ? 'pdf' : 'excel',
      }
      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      this.$apollo
        .query({
          query: EXPORT_REPORT_SUMMARY_ALERT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          this.showDownloader(periode, extension, res.data.reportSummaryAlert)
        })
    },
  },
}
</script>
